import { useState, useEffect } from 'react';
import { FaRobot, FaCloud, FaCode, FaChevronDown } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import IndustryCarousel from '../../components/IndustryCarousel/IndustryCarousel';
import CTASection from '../../components/CTASection/CTASection';
import EngagementProcess from '../../components/EngagementProcess/EngagementProcess';
import styles from './Home.module.scss';
import '../../styles/main.scss';

import aiLogoImg from '../../assets/ailogo.png';
import { HERO, KEY_FACTS, STRENGTHS } from '../../content/home';
import { CORE_VALUES } from '../../content/about';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';
import { PAGE_SEO } from '../../content/site';

const STRENGTH_ICONS = {
  robot: FaRobot,
  cloud: FaCloud,
  code: FaCode,
} as const;

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);

  // scroll action to suggest scrolling more
  const handleScrollHintClick = () => {
    window.scrollTo({
      top: window.innerHeight - 80,
      behavior: 'smooth'
    });
  }

  // Card selection state
  const [selectedFact, setSelectedFact] = useState<number | null>(null);
  const [selectedStrength, setSelectedStrength] = useState<number | null>(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className={styles.homePage}>
      <Seo {...seoPropsFor(PAGE_SEO.home)} />

      <div className={isVisible ? 'main-content' : ''}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.container}>
            <div className={styles.heroContent}>
              <h1>{HERO.headline}</h1>
              <div className={styles.heroTextBox}>
                <div className={styles.heroText}>
                  {HERO.subhead}
                </div>
              </div>
              <div className={styles.heroActions}>
                <Link to="/services" className={`${styles.btn} ${styles['btn-primary']}`}>Our Services</Link>
                <Link to="/contact" className={`${styles.btn} ${styles['btn-secondary']}`}>Get in Touch</Link>
              </div>
            </div>
          </div>
          <button
            type="button"
            className={styles.scrollHint}
            onClick={handleScrollHintClick}
            aria-label="Scroll down"
          >
            <FaChevronDown />
            <span>Scroll</span>
          </button>
        </section>

        {/* Key Facts */}
        <section className={styles.keyFacts}>
          <div className={styles.container}>
            <h2>Our Key Facts</h2>
            <div className={styles.strengthsGrid}>
              {KEY_FACTS.map((fact) => (
                <div
                  key={fact.id}
                  className={`${styles.factCard} ${selectedFact === fact.id ? styles.selected : ''}`}
                  onClick={() => setSelectedFact(selectedFact === fact.id ? null : fact.id)}
                >
                  <h3>{fact.number}</h3>
                  <p>{fact.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Strengths */}
        <section className={styles.strengths}>
          <div className={styles.container}>
            <h2>Our Strengths</h2>
            <div className={styles.strengthsGrid}>
              {STRENGTHS.map((strength) => {
                const Icon = STRENGTH_ICONS[strength.iconKey];
                return (
                  <div
                    key={strength.id}
                    className={`${styles.strengthCard} ${selectedStrength === strength.id ? styles.selected : ''}`}
                    onClick={() => setSelectedStrength(selectedStrength === strength.id ? null : strength.id)}
                  >
                    <Icon className={styles.icon} />
                    <h3>{strength.title}</h3>
                    <p>{strength.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <IndustryCarousel />

        <section className={styles.keyValues}>
          <div className={styles.container}>
            <div className={styles.valuesLayout}>
              <div className={styles.valuesImage}>
                <img src={aiLogoImg} alt="BizSolutions AI Innovation" />
              </div>

              <div className={styles.valuesContent}>
                <h2>Our Key Values</h2>
                <p className={styles.valuesIntro}>
                  Guiding principles that shape how we work and deliver value to our clients
                </p>

                <div className={styles.valuesList}>
                  {CORE_VALUES.map((value) => (
                    <div key={value.id} className={styles.valueItem}>
                      <h3>{value.title}</h3>
                      <ul className={styles.valueBullets}>
                        {value.bullets.map((bullet, i) => (
                          <li key={i}>{bullet}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The same section /services renders, from the same content. This
            page had its own markup and its own stylesheet for the identical
            steps -- see components/EngagementProcess. */}
        <EngagementProcess />

        {/* Hide Success Stories for now until further approval */}
        {/* Success Stories Section */}
        {/* <section className={styles.successStories}>
          <div className={styles.container}>
            <h2>Success Stories</h2>
            <div className={styles.storiesGrid}>
              <div className={styles.storyCard}>
                <h3>Global Retail Chain</h3>
                <p>Implemented ServiceNow across 200+ locations, reducing IT ticket resolution time by 65%.</p>
              </div>
              <div className={styles.storyCard}>
                <h3>Healthcare Provider</h3>
                <p>Custom AI solution improved patient scheduling efficiency by 40%.</p>
              </div>
            </div>
          </div>
        </section> */}
      </div>

      <CTASection
        heading="Let's Get Started"
        body="Ready to transform your operations with ServiceNow and AWS? We build the systems your business runs on, and we stay on to support them."
        ctaLabel="Tell us about your project"
        ctaHref="/contact"
      />
    </div>
  );
};

export default HomePage;
